/**
 * 1 つの KUMODeck アカウントで作る・遊ぶ — ダッシュボードの側の「React に依存しない部分」（2026-10-07 console/one-account・段 B）。
 * node --test で確かめる（oneAccount.test.ts）。画面は pages/AccountSignIn.tsx・pages/PlayerPage.tsx・pages/CreatorStart.tsx・components/ModeSwitch.tsx。
 *
 * 設計: docs/research/2026-10-07-one-kumodeck-account.md §2・§3。API の形: apps/server/src/modules/players/account/README.md の末尾。
 *
 * 旗: GET /v1/platform/info の `oneAccount: true` と `accountUrl`（Core リードと合意 2026-10-07）。
 *   どちらかが無い・false・形が変 → off と同じ = 今のログインと画面のまま（古いサーバー・旗 off の本番で何も変わらない = 10/9 の招待の流れを守る）。
 *
 * ログインの往復（ゲームの「KUMODeck でログイン」と同じ形・同じ守り）:
 *   1. verifier（32 バイトの乱数）を作って sessionStorage に置き、challenge = base64url(sha256(verifier))
 *   2. ブラウザを `<accountUrl>/account/console?challenge=…` へ
 *   3. account が `<ダッシュボード>/signin/callback#code=…` へ戻す（60 秒・一回限り）。fragment を読んだらすぐ URL から消す
 *   4. POST /v1/account/console/session { code, verifier } → { accountToken: kas_, developerToken: kds_ | null, creator, account }
 *      WHY パスが session か: 当初 README は exchange だったが、閉じた機能を見張るサーバーのテストが exchange を拾うので Core が改めた（2026-10-07）
 *
 * 鍵は 2 つ（設計 §3）:
 *   - kas_ = Player の口（/v1/account/*）だけに効く。作り手でない人もダッシュボードに入れる鍵
 *   - kds_ = 今までの作り手の鍵（お金・プロジェクト）。作り手の中身がある人だけが持つ
 *   **Player の画面は kas_ だけを送る**（accountHeaders）。api.ts の api() は kds_ を自動で付けるので、Player の口は必ず auth:false + この見出しで呼ぶ。
 *   WHY: kds_ を Player の口へ流すと、Player の画面のどこかの不具合が作り手の鍵を外へ出す道になる（逆も同じ = サーバーは kas_ 以外を 401 にする）
 */

import { isInviteError } from './inviteCode.ts';

// ---------------------------------------------------------------- 旗

export interface OneAccountInfo {
  enabled: boolean;
  /** アカウントの場所のオリジン（末尾の / なし）。enabled のときだけ文字列 */
  accountUrl: string | null;
}
export const ONE_ACCOUNT_OFF: OneAccountInfo = { enabled: false, accountUrl: null };

/** platform/info の応答全体から読む。oneAccount === true かつ accountUrl が http(s) のオリジンのときだけ on */
export function readOneAccount(info: unknown): OneAccountInfo {
  if (!info || typeof info !== 'object') return ONE_ACCOUNT_OFF;
  const o = info as { oneAccount?: unknown; accountUrl?: unknown };
  if (o.oneAccount !== true || typeof o.accountUrl !== 'string') return ONE_ACCOUNT_OFF;
  let u: URL;
  try {
    u = new URL(o.accountUrl);
  } catch {
    return ONE_ACCOUNT_OFF;
  }
  // オリジンだけを使う（サーバーの KUMO_ACCOUNT_URL も「origin only」= パスが付いていたら怪しいので off に倒す = 今のログインで入れる）
  if ((u.protocol !== 'https:' && u.protocol !== 'http:') || (u.pathname !== '/' && u.pathname !== '') || u.search || u.hash) return ONE_ACCOUNT_OFF;
  return { enabled: true, accountUrl: u.origin };
}

/** account の入口（ログインの後にダッシュボードへ戻る）。challenge は 43 文字の base64url（サーバーが形を確かめる） */
export const consoleSignInUrl = (accountUrl: string, challenge: string) => `${accountUrl}/account/console?${new URLSearchParams({ challenge })}`;

/** account のページ（入り方を足す・Cookie と CSRF の内側でだけ行う = Core リード 2026-10-07。kas_ で外のサービスへの往復は始めない） */
export const accountPageUrl = (accountUrl: string) => `${accountUrl}/account`;

// ---------------------------------------------------------------- パス

/** account から戻る場所。App.tsx はこのパスだけ、ログインの有無に関係なく同じ画面を出す（OAuth の /oauth/callback と同じ理由） */
export const SIGNIN_CALLBACK_PATH = '/signin/callback';
/** Player の画面 */
export const PLAYER_PATH = '/player';
/**
 * Player の画面のタブ（2026-10-08 console/account-pages・founder「1 枚で縦長は無理がある」）。account のページ（サーバーの /account・
 * /account/profile・/account/sign-in = players/account/tabs.ts）と同じ 3 つ: Games（PLAYER_PATH）・Profile・Sign-in（入り方 + 削除）
 */
export const PLAYER_PROFILE_PATH = `${PLAYER_PATH}/profile`;
export const PLAYER_SIGNIN_PATH = `${PLAYER_PATH}/sign-in`;
/** 作り手の中身が無い人の「招待コードを入れる」画面。サーバーの invite_code_required の details.signupUrl（/signup）もここへ寄せる */
export const START_CREATING_PATH = '/start-creating';

// ---------------------------------------------------------------- 待ち（sessionStorage）

export interface PendingSignIn {
  verifier: string;
  /** ログインの後に戻る画面（同じサイトの中のパス。afterLoginPath で確かめてから使う） */
  returnTo: string;
  createdAt: number;
}
export const SIGNIN_PENDING_KEY = 'kumo.console.signin.pending';
/** account でログインに手間取っても間に合う長さ（コードそのものは 60 秒）。古い控えは使わない */
export const SIGNIN_PENDING_TTL_MS = 15 * 60 * 1000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function savePendingSignIn(storage: StorageLike | null, p: PendingSignIn): boolean {
  try {
    if (!storage) return false;
    storage.setItem(SIGNIN_PENDING_KEY, JSON.stringify(p));
    return true;
  } catch {
    // プライベートモード等。戻ったときに「控えが無い」= もう一度押してもらう（verifier はこのタブにしか置けない）
    return false;
  }
}

/** 控えを取り出して消す（一回限り。古い・壊れた控えも消して null） */
export function takePendingSignIn(storage: StorageLike | null, now = Date.now()): PendingSignIn | null {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(SIGNIN_PENDING_KEY) ?? null;
    storage?.removeItem(SIGNIN_PENDING_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<PendingSignIn>;
    if (typeof p.verifier !== 'string' || p.verifier.length < 43 || typeof p.createdAt !== 'number') return null;
    if (now - p.createdAt > SIGNIN_PENDING_TTL_MS || p.createdAt > now + 60_000) return null;
    return { verifier: p.verifier, returnTo: typeof p.returnTo === 'string' ? p.returnTo : '/', createdAt: p.createdAt };
  } catch {
    return null;
  }
}

/** 戻りの fragment（#code=… / #error=…）を読む */
export function parseSignInCallback(hash: string): { code: string | null; error: string | null } {
  const h = new URLSearchParams(hash.replace(/^#/, ''));
  const code = h.get('code');
  const error = h.get('error');
  return { code: code && /^[A-Za-z0-9_-]{20,200}$/.test(code) ? code : null, error: error && /^[a-z_]{1,64}$/.test(error) ? error : null };
}

// ---------------------------------------------------------------- 交換の応答

export interface ConsoleSession {
  accountToken: string;
  developerToken: string | null;
  creator: boolean;
  account: { email: string | null; displayName: string | null };
}

/** POST /v1/account/console/session の応答を確かめる。kas_ / kds_ の形でない鍵は持たない（取り違えて別の口へ送らない） */
export function readConsoleSession(r: unknown): ConsoleSession | null {
  if (!r || typeof r !== 'object') return null;
  const o = r as Record<string, unknown>;
  if (typeof o.accountToken !== 'string' || !o.accountToken.startsWith('kas_')) return null;
  const dev = typeof o.developerToken === 'string' && o.developerToken.startsWith('kds_') ? o.developerToken : null;
  const a = (o.account && typeof o.account === 'object' ? o.account : {}) as Record<string, unknown>;
  return {
    accountToken: o.accountToken,
    developerToken: dev,
    // creator は鍵があるときだけ true（応答の印だけ true でも kds_ が無ければ作り手の画面は何も読めない）
    creator: dev !== null,
    account: { email: typeof a.email === 'string' ? a.email : null, displayName: typeof a.displayName === 'string' ? a.displayName : null }
  };
}

// ---------------------------------------------------------------- Player の口の見出し

/**
 * Player の口（/v1/account/*）に送る Authorization。kas_ でなければ投げる（kds_ を渡す取り違えをここで止める）。
 * WHY 投げるか: 黙って送らないと 401 → 「ログインし直して」になり、原因（鍵の取り違え）が見えない
 */
export function accountHeaders(accountToken: string): Record<string, string> {
  if (!accountToken.startsWith('kas_')) throw new Error('The Player side only sends the account session (kas_)');
  return { authorization: `Bearer ${accountToken}` };
}

// ---------------------------------------------------------------- Player / Creator の切り替え

export type ConsoleMode = 'player' | 'creator';
export const MODE_KEY = 'kumo.console.mode';

/** 最後に選んだ方（端末ごと・便利さだけ = 読めなくても動く） */
export function readSavedMode(storage: Pick<Storage, 'getItem'> | null): ConsoleMode | null {
  try {
    const v = storage?.getItem(MODE_KEY);
    return v === 'player' || v === 'creator' ? v : null;
  } catch {
    return null;
  }
}
export function saveMode(storage: Pick<Storage, 'setItem'> | null, mode: ConsoleMode): void {
  try {
    storage?.setItem(MODE_KEY, mode);
  } catch {
    /* 保存できなくても切り替えはできる（次に開いたとき既定に戻るだけ） */
  }
}

/** 開く方: 最後に選んだ方 → 無ければ作り手の中身があれば Creator・無ければ Player（設計 §3） */
export const defaultMode = (creator: boolean, saved: ConsoleMode | null): ConsoleMode => saved ?? (creator ? 'creator' : 'player');

/** 方ごとの最初の画面。Creator でも作り手の中身が無ければ「招待コードを入れる」画面 */
export const modeHome = (mode: ConsoleMode, creator: boolean) => (mode === 'player' ? PLAYER_PATH : creator ? '/' : START_CREATING_PATH);

// ---------------------------------------------------------------- 作り手を始める（POST /v1/account/creator）のエラー

/**
 * 招待コードの画面に出す案内の種類。
 *   email_required       = メールの無いアカウント（X だけで作った等）→ account のページでメールを足してから
 *   creator_email_in_use = 同じメールの今の作り手がいる（自動でまとめない = 設計 §1）→ 前の入り方で入り直す
 *   already_creator      = もう作り手（別のタブで始めた等）→ 入り直せば Creator が開く
 *   invite               = 招待コードの誤り（inviteCode.ts の INVITE_ERRORS。文言は既存の invite.* を使う）
 */
export type CreatorStartIssue = 'email_required' | 'creator_email_in_use' | 'already_creator' | 'invite' | 'other';
export function creatorStartIssue(code: string | undefined): CreatorStartIssue {
  if (code === 'email_required' || code === 'creator_email_in_use' || code === 'already_creator') return code;
  if (isInviteError(code)) return 'invite';
  return 'other';
}

// ---------------------------------------------------------------- 前のログイン用パスキー（設計 §2）

/**
 * 今のログイン用パスキー（RP ID = ダッシュボードのホスト）で入った作り手に「1 回登録し直して」を出すための印。
 * このタブの間だけ（sessionStorage）。WHY: 移行の間はダッシュボードで古いパスキーを受けるが、account では使えない。
 *   次に account で入るときに困らないよう、入った直後に 1 回だけ案内する（閉じたら出さない）
 */
export const OLD_PASSKEY_KEY = 'kumo.console.oldPasskey';
